import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _typeof from "@babel/runtime/helpers/typeof";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.reverse.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useDebounce } from 'use-debounce';
import { useVirtual } from 'react-virtual';
import { useTranslation } from 'react-i18next';
import { isIdType, isObjectWithId } from 'types/utils';
import Box from 'components/Box';
import useBreakpoints from 'hooks/useBreakpoints';
import Flex from 'components/Flex';
import EmptyStateController from 'components/EmptyStateController';
import Landmark from 'components/Landmark';
import useItemPickerView, { VIEW_KEYS } from './useItemPickerView';
import ItemPickerUI, { DEFAULT_CONTENT_HEIGHT } from './ItemPickerUI';
import ListItem from './ListItem';
import { jsx as _jsx } from "react/jsx-runtime";
var DEBOUNCE_INTERVAL = 500; // no more than 1 search query every 0.5 seconds

function defaultGetItemKey(item) {
  if (isIdType(item)) {
    return item;
  }
  if (isObjectWithId(item)) {
    return item.id;
  }
  throw Error('ItemPicker: Could not get item key. Please provide a getItemKey function.');
}
function defaultGetItemLabel(item) {
  if (item && _typeof(item) === 'object' && 'name' in item && typeof item.name === 'string') {
    return item.name;
  }
  throw Error('ItemPicker: Could not get item label. Please provide a working renderItemLabel.');
}
function ItemPicker(props) {
  var _props$isSingleItemPi = props.isSingleItemPicker,
    isSingleItemPicker = _props$isSingleItemPi === void 0 ? false : _props$isSingleItemPi,
    isDisabled = props.isDisabled,
    title = props.title,
    items = props.items,
    _props$disabledItems = props.disabledItems,
    disabledItems = _props$disabledItems === void 0 ? [] : _props$disabledItems,
    _props$defaultSelecte = props.defaultSelectedItems,
    defaultSelectedItems = _props$defaultSelecte === void 0 ? [] : _props$defaultSelecte,
    _props$defaultIndeter = props.defaultIndeterminateItems,
    defaultIndeterminateItems = _props$defaultIndeter === void 0 ? [] : _props$defaultIndeter,
    hasNextPage = props.hasNextPage,
    onFetchNextPage = props.onFetchNextPage,
    isFetchingNextPage = props.isFetchingNextPage,
    isError = props.isError,
    isLoading = props.isLoading,
    _props$getItemKey = props.getItemKey,
    getItemKey = _props$getItemKey === void 0 ? defaultGetItemKey : _props$getItemKey,
    customLabels = props.labels,
    _props$renderItemLabe = props.renderItemLabel,
    renderItemLabel = _props$renderItemLabe === void 0 ? defaultGetItemLabel : _props$renderItemLabe,
    renderItemSubtitle = props.renderItemSubtitle,
    renderItemIcon = props.renderItemIcon,
    renderInlineIcon = props.renderInlineIcon,
    renderFilterMenu = props.renderFilterMenu,
    onSearchValueChange = props.onSearchValueChange,
    onSubmit = props.onSubmit,
    onClose = props.onClose,
    _props$onBeforeChange = props.onBeforeChange,
    onBeforeChange = _props$onBeforeChange === void 0 ? function () {
      return Promise.resolve();
    } : _props$onBeforeChange,
    _props$contentHeight = props.contentHeight,
    contentHeight = _props$contentHeight === void 0 ? DEFAULT_CONTENT_HEIGHT : _props$contentHeight,
    _props$allowSubmitWit = props.allowSubmitWithoutChanges,
    allowSubmitWithoutChanges = _props$allowSubmitWit === void 0 ? false : _props$allowSubmitWit;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(defaultSelectedItems),
    _useState2 = _slicedToArray(_useState, 2),
    selectedItems = _useState2[0],
    setSelectedItems = _useState2[1];
  // indeterminateItems are not taken into consideration / displayed when SingleItemPicker is used
  var _useState3 = useState(isSingleItemPicker ? [] : defaultIndeterminateItems),
    _useState4 = _slicedToArray(_useState3, 2),
    indeterminateItems = _useState4[0],
    setIndeterminateItems = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    searchValue = _useState6[0],
    setSearchValue = _useState6[1];
  var _useDebounce = useDebounce(searchValue, DEBOUNCE_INTERVAL),
    _useDebounce2 = _slicedToArray(_useDebounce, 1),
    debouncedSearchValue = _useDebounce2[0];
  useEffect(function () {
    // Provide parent with debounced SearchValue
    onSearchValueChange === null || onSearchValueChange === void 0 || onSearchValueChange(debouncedSearchValue);
  }, [debouncedSearchValue, onSearchValueChange]);
  var _useItemPickerView = useItemPickerView({
      title: title,
      searchValue: searchValue,
      clearSearchValue: function clearSearchValue() {
        return setSearchValue('');
      }
    }),
    view = _useItemPickerView.view,
    changeView = _useItemPickerView.changeView;
  var selectedAndIndeterminateItems = [].concat(_toConsumableArray(selectedItems), _toConsumableArray(indeterminateItems));
  var wasItemSelectedBefore = function wasItemSelectedBefore(item) {
    return !!defaultSelectedItems.find(function (other) {
      return getItemKey(item) === getItemKey(other);
    });
  };
  var isItemSelected = function isItemSelected(item) {
    return !!selectedItems.find(function (other) {
      return getItemKey(item) === getItemKey(other);
    });
  };
  var isItemIndeterminate = function isItemIndeterminate(item) {
    return !!indeterminateItems.find(function (other) {
      return getItemKey(item) === getItemKey(other);
    });
  };
  var isItemDisabled = function isItemDisabled(item) {
    return !!disabledItems.find(function (other) {
      return getItemKey(item) === getItemKey(other);
    });
  };

  // if this logic looks incomplete, it's because when calculating `toAdd` it's not taking into account items
  // that are indeterminate *now*. That's ok. Because of how the checkboxes behave, an item can never *become*
  // indeterminate, so indeterminateItems can never have any new entries.
  var toAdd = selectedItems.filter(function (item) {
    return !wasItemSelectedBefore(item);
  });
  var toRemove = [].concat(_toConsumableArray(defaultSelectedItems.filter(function (item) {
    return !isItemSelected(item);
  })), _toConsumableArray(defaultIndeterminateItems.filter(function (item) {
    return !isItemIndeterminate(item) && !isItemSelected(item);
  })));
  var isNewSelection = !!toAdd.length || !!toRemove.length;
  var submit = function submit(e) {
    e.stopPropagation();
    if (isSingleItemPicker) {
      onSubmit({
        toAdd: toAdd[0],
        toRemove: toRemove[0],
        selectedItems: selectedItems[0]
      });
    } else {
      onSubmit({
        toAdd: toAdd,
        toRemove: toRemove,
        selectedItems: selectedItems
      });
    }
  };
  var handleChange = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(changedItem, isSelected) {
      var changes, filterChangedItem, _t;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            changes = {
              toAdd: isSelected ? [changedItem] : [],
              toRemove: isSelected ? [] : [changedItem]
            };
            _context.prev = 1;
            _context.next = 2;
            return onBeforeChange(changes);
          case 2:
            if (isSingleItemPicker) {
              setSelectedItems([changedItem]);
            } else {
              // The following will only be run if the changes get approved…
              filterChangedItem = function filterChangedItem(item) {
                return getItemKey(item) !== getItemKey(changedItem);
              };
              setSelectedItems(function (items) {
                return isSelected ? items.concat(changedItem) : items.filter(filterChangedItem);
              });
              setIndeterminateItems(function (items) {
                return items.filter(filterChangedItem);
              });
            }
            _context.next = 4;
            break;
          case 3:
            _context.prev = 3;
            _t = _context["catch"](1);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 3]]);
    }));
    return function handleChange(_x, _x2) {
      return _ref.apply(this, arguments);
    };
  }();
  var labels = useMemo(function () {
    return _objectSpread({}, customLabels);
  }, [customLabels]);
  var isSelectionView = view.key === VIEW_KEYS.SELECTED;
  var itemsToRender = isSelectionView ? selectedAndIndeterminateItems : items;
  var parentRef = useRef(null);
  var _useBreakpoints = useBreakpoints(),
    xxl = _useBreakpoints.xxl;
  var rowVirtualizer = useVirtual({
    size: itemsToRender.length + (hasNextPage && !isSelectionView ? 1 : 0),
    parentRef: parentRef,
    estimateSize: React.useCallback(function () {
      return xxl ? 77 : 48;
    }, [xxl]),
    overscan: 5
  });
  useEffect(function () {
    var _reverse = _toConsumableArray(rowVirtualizer.virtualItems).reverse(),
      _reverse2 = _slicedToArray(_reverse, 1),
      lastItem = _reverse2[0];
    if (!lastItem) {
      return;
    }
    if (lastItem.index > itemsToRender.length - 1 && hasNextPage && !isLoading && !isFetchingNextPage && onFetchNextPage) {
      onFetchNextPage();
    }
  }, [hasNextPage, isLoading, itemsToRender.length, onFetchNextPage, rowVirtualizer.virtualItems, isFetchingNextPage]);
  return /*#__PURE__*/_jsx(ItemPickerUI, {
    isSubmitDisabled: isDisabled || !allowSubmitWithoutChanges && !isNewSelection,
    isSingleItemPicker: isSingleItemPicker,
    view: view,
    onChangeView: changeView,
    labels: labels,
    selectedItemCount: selectedAndIndeterminateItems.length,
    searchValue: searchValue,
    onSearchValueChange: setSearchValue,
    onSubmit: submit,
    onClose: onClose,
    renderFilterMenu: renderFilterMenu,
    contentHeight: contentHeight,
    scrollContainerRef: parentRef,
    children: /*#__PURE__*/_jsx(EmptyStateController, {
      isLoading: isLoading,
      isError: isError,
      isEmpty: !isLoading && itemsToRender.length === 0,
      emptyContent: /*#__PURE__*/_jsx(Landmark, {
        children: t('error_empty')
      }),
      children: /*#__PURE__*/_jsx(Box, {
        role: "group",
        "aria-label": view.title,
        position: "relative",
        width: "100%",
        height: rowVirtualizer.totalSize,
        children: rowVirtualizer.virtualItems.map(function (virtualRow) {
          var item = itemsToRender[virtualRow.index];
          var isLoaderRow = virtualRow.index > itemsToRender.length - 1;
          var style = {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: virtualRow.size,
            transform: "translateY(".concat(virtualRow.start, "px)")
          };
          if (isLoaderRow) {
            return /*#__PURE__*/_jsx(Flex, {
              p: "xxs",
              style: style,
              bold: true,
              pl: 22,
              children: t('loading')
            }, virtualRow.index);
          }
          if (!item) {
            return null;
          }
          return /*#__PURE__*/_jsx(ListItem, {
            isSingleItemPicker: isSingleItemPicker,
            id: getItemKey(item),
            label: renderItemLabel(item),
            subtitle: renderItemSubtitle === null || renderItemSubtitle === void 0 ? void 0 : renderItemSubtitle(item),
            icon: renderItemIcon === null || renderItemIcon === void 0 ? void 0 : renderItemIcon(item),
            inlineIcon: renderInlineIcon === null || renderInlineIcon === void 0 ? void 0 : renderInlineIcon(item),
            isSelected: isItemSelected(item),
            isIndeterminate: isItemIndeterminate(item),
            isDisabled: isItemDisabled(item),
            onChange: function onChange(newState) {
              return handleChange(item, newState);
            },
            style: style
          }, virtualRow.index);
        })
      })
    })
  });
}
export default ItemPicker;